import { Download } from 'lucide-react'
import Button from './Button'
import FileField from './FileField'
import { InfoNote } from './Form'
import './ExcelUploadFields.css'

/* The spreadsheet MIME types an `accept` may name, by the extension a reader knows. */
const MIME_EXTENSION = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
  'text/csv': 'csv',
}

/* One order for the format tags on every import, whatever order `accept` lists them in. */
const FORMAT_ORDER = ['XLSX', 'XLS', 'CSV']
const formatRank = (format) => {
  const rank = FORMAT_ORDER.indexOf(format)
  return rank === -1 ? FORMAT_ORDER.length : rank
}

/**
 * The excel-upload body the legacy repeats verbatim in several modals: the rule about
 * the header row, the required-columns table with its template, and the file input.
 *
 * Strings are passed in rather than baked in, because the wording differs slightly
 * between callers — the group bulk update accepts .xlsx only, the user import also
 * takes .csv.
 *
 * @param {{name: string, required: boolean}[]} columns
 */
export default function ExcelUploadFields({
  id,
  name = 'excelFile',
  accept,
  columns,
  templateHref,
  onDownloadTemplate,
  error,
  onChange,
  strings,
}) {
  /* The extensions the picker takes, said under it (2026-10-22): ".xls,.xlsx,.csv"
     becomes XLS · XLSX · CSV. A caller that names spreadsheet MIME types instead gets
     their extensions; any other MIME type is left out — it means nothing to a reader. */
  const formats = [
    ...new Set(
      (accept ?? '')
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .map((item) => (item.startsWith('.') ? item.slice(1) : MIME_EXTENSION[item]))
        .filter(Boolean)
        .map((item) => item.toUpperCase()),
    ),
  ].sort((a, b) => formatRank(a) - formatRank(b))

  /* The template as a small button in the identity: a file to take, so it is an
     action, not a link in the text. A caller that builds the file itself downloads on
     click. */
  const templateButton = (
    <Button
      variant="primary"
      size="sm"
      className="excel-upload__template"
      {...(onDownloadTemplate
        ? { onClick: onDownloadTemplate }
        : { as: 'a', href: templateHref ?? '#', download: true })}
    >
      <Download size={14} strokeWidth={2} aria-hidden="true" />
      {strings.template}
    </Button>
  )

  return (
    /* One wrapper, so the softened note and the spacing below stay this body's own. */
    <div className="excel-upload">
      {/* The rule first, as a compact note at the top (2026-10-22): it is what the file
          has to satisfy, read before the columns it refers to — no lead-in, the
          sentence says it. */}
      <InfoNote className="excel-upload__note">
        <bdi>{strings.notes}</bdi>
      </InfoNote>

      {/* Not every import states its columns. The questions upload just points
          at a template and takes the file back, so the spec card is skipped
          rather than rendered as an empty table. */}
      {columns?.length > 0 && (
      <section className="excel-upload__columns">
        {/* The template belongs beside the spec it implements — "these are the
            columns" and "here is a file that already has them" are one thought. */}
        <header className="excel-upload__columns-head">{templateButton}</header>

        <div className="excel-upload__scroll">
          <table>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.name} scope="col">
                    {/* `dir` sits on a SPAN, not on the cell. On the cell it also
                        decides where the text sits, so every header parked
                        itself against its cell's left edge while the value
                        underneath sat against the right — one column, two
                        opposite edges, up to 270px apart. The span keeps the
                        Latin ordering and leaves the alignment to the sheet. */}
                    <span dir="ltr">{column.name}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {columns.map((column) => (
                  <td
                    key={column.name}
                    className={column.required ? 'is-required' : undefined}
                  >
                    <bdi>{column.required ? strings.mandatory : strings.optional}</bdi>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </section>
      )}

      {!columns?.length && (templateHref || onDownloadTemplate) && (
        <div className="excel-upload__standalone-template">{templateButton}</div>
      )}

      <div className="excel-upload__file">
        {/* The formats ride inside the drop zone, beside its hint. */}
        <FileField id={id} name={name} accept={accept} onChange={onChange} formats={formats} />
        {error && (
          <p className="form-row__error" role="alert">
            <bdi>{error}</bdi>
          </p>
        )}
      </div>
    </div>
  )
}
